import * as React from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type SocialAuthProvider = 'google' | 'microsoft';

/*
 * The providers' own marks, as their brand guidelines publish them. These are the one place a
 * hex value is allowed: a Google "G" in the brand purple is no longer Google's mark.
 */
const MARKS: Record<SocialAuthProvider, React.ReactNode> = {
	google: (
		<svg viewBox="0 0 48 48" className="h-4 w-4 shrink-0" aria-hidden="true" focusable="false">
			<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
			<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
			<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
			<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
		</svg>
	),
	microsoft: (
		<svg viewBox="0 0 21 21" className="h-4 w-4 shrink-0" aria-hidden="true" focusable="false">
			<rect x="1" y="1" width="9" height="9" fill="#F25022" />
			<rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
			<rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
			<rect x="11" y="11" width="9" height="9" fill="#FFB900" />
		</svg>
	),
};

const LABELS: Record<SocialAuthProvider, string> = {
	google: 'Continue with Google',
	microsoft: 'Continue with Microsoft',
};

export interface SocialAuthButtonProps extends Omit<ButtonProps, 'variant' | 'icon' | 'children' | 'asChild'> {
	provider: SocialAuthProvider;
	/** Defaults to "Continue with Google" / "Continue with Microsoft", which reads right on sign-in and sign-up alike. */
	label?: string;
}

/**
 * "Continue with Google / Microsoft": a full-width outline `Button` carrying the provider's mark.
 *
 * Sign-in and sign-up each drew their own pill-shaped copy with a lookalike logo and swapped the
 * label for "Signing you in..." while busy, so the button changed width mid-click. This keeps the
 * 8px control radius, the official marks, and `pending` from `Button`: the spinner takes the
 * mark's place, the label stays, `aria-busy` is set.
 */
export const SocialAuthButton = React.forwardRef<HTMLButtonElement, SocialAuthButtonProps>(function SocialAuthButton(
	{ provider, label, className, type = 'button', ...props },
	ref,
) {
	return (
		<Button ref={ref} type={type} variant="outline" icon={MARKS[provider]} className={cn('w-full', className)} {...props}>
			{label ?? LABELS[provider]}
		</Button>
	);
});
